'use client';

import { FC, useState, useEffect } from 'react';
import Link from 'next/link';
import BlogReactions from '@/components/blog/BlogReactions';
import { FormattedBlogPost } from '@/lib/blog/blog-utils'; 
import { FaShareAlt, FaFacebookF, FaTwitter, FaLinkedinIn } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

interface BlogCardProps {
  post: FormattedBlogPost;
}

const BlogCard: FC<BlogCardProps> = ({ post }) => {
  const [shareCount, setShareCount] = useState<number>(post.shareCount);
  const [origin, setOrigin] = useState('');

  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);

  const ogImageUrl = `${origin}/api/og/${post.slug}`;

  useEffect(() => {
    const fetchShareCount = async () => {
      try {
        const res = await fetch(`/api/blog/${post.slug}/share`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        setShareCount(data.shares ?? post.shareCount);
      } catch {
        console.log('Failed to fetch share count for', post.slug);
      }
    };
    fetchShareCount();
  }, [post.slug, post.shareCount]);

  const incrementShare = async (platform?: string) => {
    try {
      const res = await fetch(`/api/blog/${post.slug}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform }),
      });
      const data = await res.json();
      if (res.ok) setShareCount(data.shares ?? shareCount);
    } catch (err) {
      console.error('Increment share failed:', err);
    }
  };

  const handleNativeShare = async () => {
    const url = `${origin}/blog/${post.slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, url });
        incrementShare('native');
      } else {
        await navigator.clipboard.writeText(url);
        toast.info('Link copied to clipboard!');
        incrementShare('copy-link');
      }
    } catch {
      toast.error('Failed to share post');
    }
  };

  const shareToFacebook = () => {
    const url = encodeURIComponent(`${origin}/blog/${post.slug}`);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=500');
    incrementShare('facebook');
  };

  const shareToTwitter = () => {
    const url = encodeURIComponent(`${origin}/blog/${post.slug}`);
    const text = encodeURIComponent(post.title);
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'width=600,height=400');
    incrementShare('twitter');
  };

  const shareToLinkedIn = () => {
    const url = encodeURIComponent(`${origin}/blog/${post.slug}`);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=600,height=500');
    incrementShare('linkedin');
  };

  const readingTime = Math.ceil(post.content.split(/\s+/).length / 200);

  return (
    <motion.div
      className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl flex flex-col h-full relative overflow-hidden w-full min-w-0"
      whileHover={{ y: -6 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      style={{ boxShadow: '0 10px 30px -15px rgba(0,0,0,0.1)' }}
    >
      {post.coverImage && (
        <Link href={`/blog/${post.slug}`} className="block w-full h-52 overflow-hidden">
          <motion.img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover max-w-full"
            whileHover={{ scale: 1.08 }}
            transition={{ duration: 0.4 }}
          />
        </Link>
      )}

      <div className="flex flex-col p-6 flex-1 min-w-0">
        <div className="flex justify-between items-center mb-4">
          {post.category && (
            <span className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-[10px] uppercase font-bold tracking-widest px-2.5 py-1 rounded-md">
              {post.category}
            </span>
          )}
          <span className="text-zinc-400 dark:text-zinc-500 text-xs font-medium">
            {readingTime} min read
          </span>
        </div>

        <Link href={`/blog/${post.slug}`} className="min-w-0">
          <h2
            className="text-xl font-bold mb-3 text-zinc-900 dark:text-zinc-100 line-clamp-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            title={post.title} // <-- Tooltip shows full title on hover
          >
            {post.title}
          </h2>
        </Link>

        <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed mb-6 line-clamp-3 flex-1 min-w-0">
          {post.excerpt}
        </p>

        <div className="flex flex-wrap items-center justify-between mb-6 pt-4 border-t border-zinc-100 dark:border-zinc-800">
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-bold text-zinc-900 dark:text-zinc-200">{post.author}</span>
            <span className="text-[10px] text-zinc-400 uppercase tracking-tighter">
              {new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
          <BlogReactions slug={post.slug} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            onClick={handleNativeShare}
            className="flex items-center gap-2 text-xs font-bold text-zinc-600 dark:text-zinc-400 hover:text-blue-600 transition"
          >
            <FaShareAlt className="text-sm" /> {shareCount} Shares
          </button>
          
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { icon: <FaFacebookF />, action: shareToFacebook, bg: 'hover:bg-blue-600' },
              { icon: <FaTwitter />, action: shareToTwitter, bg: 'hover:bg-sky-500' },
              { icon: <FaLinkedinIn />, action: shareToLinkedIn, bg: 'hover:bg-blue-700' },
            ].map((social, i) => (
              <button
                key={i}
                onClick={social.action}
                className={`p-2 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:text-white ${social.bg} transition-all duration-300`}
              >
                <span className="text-xs">{social.icon}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BlogCard;
